import { Table, TableColumnsType } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getEnrolledStudentList } from '../../../api/statistics.api';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import exportTable from '../../../lib/exportTable';
import { addHyphen } from '../../../lib/util';
import {
  IEnrolledStudent,
  IGetEnrolledStudentList,
} from '../../../models/statistics.model';
import DownloadButton from '../../DownloadButton';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import Pagination from '../../Pagination';
import useUtil from '../../../hooks/useUtil';
import Checkbox from '../../Checkbox';
import { RootState } from '../../../store/rootReducer';

interface IProps {
  data: any;
  total: number;
  pageNo: number;
  setPageNo: (pageNo: number) => void;
  semesterCode: number | undefined;
  levelCode: number | undefined;
  courseCode: number | undefined;
  campusCodes: string[];
  date: string;
  onDataChange: any;
  searchClick: any;
}

const List: FC<IProps> = ({
  data,
  total,
  pageNo,
  setPageNo,
  semesterCode,
  levelCode,
  courseCode,
  campusCodes,
  date,
  onDataChange,
  searchClick,
}) => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { getCommonCodeLabel } = useUtil();
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetEnrolledStudentList>();
  const [checkPage, setCheckPage] = useState(true);

  useEffect(() => {
    if (searchClick) {
      console.log('searchClick true', searchClick);
      setCheckPage(searchClick);
      onDataChange(searchClick);
    }
  }, [searchClick]);

  const sendDataToParent = (checked: boolean) => {
    setCheckPage(!checked);
    onDataChange(!checked);
  };

  useEffect(() => {
    if (res?.data) {
      const _data: IEnrolledStudent[] = _.cloneDeep(res.data);
      _data.forEach((item: any) => {
        item.eduStepCode = getCommonCodeLabel('PR0006', item.eduStepCode);
        item.statsStudentStatus = getCommonCodeLabel(
          'ST0001',
          item.statsStudentStatus,
        );
        item.classStudentStatusCode = getCommonCodeLabel(
          'CL0003',
          item.classStudentStatusCode,
        );
        item.gradeCode = getCommonCodeLabel('SY0006', item.gradeCode);
        item.mobilePhone = addHyphen(item.mobilePhone);
        item.parentsMobile = addHyphen(item.parentsMobile);
        item.publisherCode =
          getCommonCodeLabel('SY0014', item.publisherCode) || '-';
        item.siblingName = item.siblingName === null ? '-' : item.siblingName;
        item.busName = item.busName === null ? '-' : item.busName;
        item.orderStt = item?.orderStt === 'Y' ? 'O' : 'X';
      });
      const excelColumns = _.cloneDeep(columns);

      excelColumns.push({
        title: t('statistics.studentEnrolled.1'),
        dataIndex: 'address',
      });
      excelColumns.push({
        title: t('statistics.studentEnrolled.2'),
        dataIndex: 'mobilePhone',
      });
      excelColumns.push({
        title: t('statistics.studentEnrolled.3'),
        dataIndex: 'parentsMobile',
      });
      excelColumns.push({
        title: t('statistics.studentEnrolled.4'),
        dataIndex: 'publisherCode',
      });
      excelColumns.push({
        title: t('campus.shuttle.column1'),
        dataIndex: 'busName',
      });
      excelColumns.push({
        title: t('statistics.studentEnrolled.12'),
        dataIndex: 'siblingName',
      });

      // excelColumns.push({
      //   title: t('statistics.studentEnrolled.13'),
      //   dataIndex: 'orderStt',
      // });

      const instructorNameColumn = {
        title: t('class.class.column25'),
        dataIndex: 'instructorName',
      };

      const insertClassCodeIndex = excelColumns.findIndex(
        (item: any) => item.dataIndex === 'statsStudentStatus',
      );

      excelColumns.splice(insertClassCodeIndex, 0, instructorNameColumn);

      const currentDate = moment(new Date());
      exportTable(
        excelColumns,
        _data,
        `${t('statistics.studentEnroll.title1')}(${t(
          'statistics.homework.tab1',
        )})_(${currentDate.format('YYYYMMDD')})`,
        t('statistics.homework.tab1'),
        [
          12, 10, 20, 20, 12, 20, 12, 20, 10, 10, 30, 15, 10, 20, 10, 60, 15,
          15, 15, 15, 15,
        ],
      );
    }
  }, [res]);

  const onExportTable = () => {
    if (semesterCode) {
      execute(
        getEnrolledStudentList({
          semesterCode,
          levelCode,
          courseCode,
          campusCode:
            auth.hqYn === 'Y'
              ? campusCodes
              : campusCodes.length
              ? campusCodes
              : auth.campus,
          date,
          pageNo: 1,
          perPage: 999999999,
          isExcel: 'Y',
        }),
      );
    }
  };

  const columns: TableColumnsType<IEnrolledStudent> = [
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusKorName',
      align: 'center',
    },
    {
      title: t('common.label.1'),
      dataIndex: 'eduStepCode',
      align: 'center',
      render: (value) => getCommonCodeLabel('PR0006', value),
    },
    {
      title: t('student.student.column5'),
      dataIndex: 'memberKorName',
      align: 'center',
    },
    {
      title: t('student.student.column6'),
      dataIndex: 'memberEngName',
      align: 'center',
    },
    {
      title: t('auth.login.label1'),
      dataIndex: 'webId',
      align: 'center',
    },
    {
      title: t('student.student.column64'),
      dataIndex: 'memberCode',
      align: 'center',
    },
    {
      title: t('student.student.column27'),
      dataIndex: 'courseName',
      align: 'center',
    },
    {
      title: t('common.menu.class'),
      dataIndex: 'className',
      align: 'center',
    },
    {
      title: t('statistics.studentEnrolled.5'),
      dataIndex: 'classRoomName',
      align: 'center',
    },
    {
      title: t('statistics.studentEnrolled.6'),
      dataIndex: 'chargeTeacherKorName',
      align: 'center',
    },
    {
      title: t('common.column.category'),
      dataIndex: 'statsStudentStatus',
      align: 'center',
      render: (value) => getCommonCodeLabel('ST0001', value, 'ko'),
    },
    {
      title: t('student.student.column4'),
      dataIndex: 'classStudentStatusCode',
      align: 'center',
      render: (value) => getCommonCodeLabel('CL0003', value, 'ko'),
    },
    {
      title: t('statistics.studentEnrolled.13'),
      dataIndex: 'orderStt',
      align: 'center',
      render: (value, record) => {
        if (record.orderStt == 'Y') {
          return 'O';
        } else {
          return 'X';
        }
      },
    },
    {
      title: t('student.student.column2'),
      dataIndex: 'schoolName',
      align: 'center',
    },
    {
      title: t('student.student.column3'),
      dataIndex: 'gradeCode',
      align: 'center',
      render: (value) => getCommonCodeLabel('SY0006', value, 'ko'),
    },
  ];

  return (
    <>
      {isLoading && <Loader />}
      <ErrorResponse error={error} />
      <div className="comm-bx__between">
        <div className="comm-sch_result">Total: {<b>{total}</b>}</div>
        <div className="comm-btn__area right">
          <Checkbox
            checked={checkPage}
            onChange={() => sendDataToParent(checkPage)}
            text={'Pagination'}
          />
        </div>
      </div>
      <div className="comm-table__area mt-0">
        <Table
          columns={columns}
          dataSource={data}
          pagination={{
            current: 1,
            pageSize: total,
          }}
        />
      </div>
      {checkPage ? (
        <div className="comm-bx__between">
          <DownloadButton onClick={onExportTable} />
          <div className="common-paging center">
            <Pagination count={total || 0} page={pageNo} setPage={setPageNo} />
          </div>
        </div>
      ) : (
        <div className="comm-bx__between">
          <DownloadButton onClick={onExportTable} />
        </div>
      )}
    </>
  );
};

export default List;
